/**
 * Launch a saved development: pick one scenario version and freeze its P&L.
 */
import { useEffect, useState } from 'react';

export function ApproveBaselineDialog({
  open,
  scenarios = [],
  activeScenarioId,
  isDraft,
  busy = false,
  error = '',
  onClose,
  onConfirm,
}) {
  const [scenarioId, setScenarioId] = useState(activeScenarioId || '');

  useEffect(() => {
    if (open) setScenarioId(activeScenarioId || scenarios[0]?.id || '');
  }, [open, activeScenarioId, scenarios]);

  if (!open) return null;
  const selected = scenarios.find((s) => s.id === scenarioId) || scenarios[0];

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-3"
      data-testid="approve-baseline-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="approve-baseline-title"
    >
      <button type="button" className="absolute inset-0" aria-label="Close" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-xl sm:rounded-lg border border-gray-200 shadow-xl p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <h2 id="approve-baseline-title" className="text-base font-semibold text-gray-900">Approve baseline</h2>
        <p className="text-[13px] text-gray-500 mt-1 leading-snug">
          Snapshot this version’s P&amp;L categories as the read-only budget.
          Later evaluation edits will not change Cash Tracker history.
        </p>
        {isDraft ? (
          <p className="mt-3 text-[13px] text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2" data-testid="approve-baseline-save-first">
            Save this development first, then approve a baseline to open Cash Tracker.
          </p>
        ) : (
          <div className="mt-3 space-y-2">
            <label className="text-[13px] text-gray-500 font-medium" htmlFor="approve-baseline-version">Version</label>
            <select
              id="approve-baseline-version"
              value={selected?.id || ''}
              onChange={(e) => setScenarioId(e.target.value)}
              className="w-full h-11 rounded-md border border-gray-200 bg-white px-3 text-base"
              data-testid="approve-baseline-version"
            >
              {scenarios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.ordinal}. {s.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {error ? <p className="mt-2 text-[12px] text-red-700">{error}</p> : null}
        <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 min-h-11 text-sm text-gray-600 hover:text-gray-800 px-3 py-2 rounded-md hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isDraft || busy || !selected}
            onClick={() => selected && onConfirm(selected.id)}
            className="flex-1 min-h-11 text-sm text-white bg-gray-900 hover:bg-gray-800 px-3 py-2 rounded-md font-medium disabled:opacity-50"
            data-testid="approve-baseline-confirm"
          >
            {busy ? 'Approving…' : 'Approve baseline'}
          </button>
        </div>
      </div>
    </div>
  );
}
